import _ from 'lodash';
import { Session, Invitation, SIPExtension } from 'sip.js';
import { SessionManager, SimpleUser } from 'sip.js/lib/platform/web';
import { IVoiceEvents } from '@/types';

/* eslint-disable @typescript-eslint/ban-ts-comment */
/** We use or override some pseudo-public methods of default SIP client */
export class WebRTCClient {
  public static readonly DEFAULT_WSS_ADDRESS = 'wss://webrtc.qa.onereach.ai:8081';
  public static readonly DEFAULT_DOMAIN = 'webrtc.qa.onereach.ai';
  public static isDebug = false;

  /* Values provided by Action Desk / Dialer */
  public static wssAddress = '';
  public static domain = '';
  public static token = '';
  public static userId = '';
  public static handlers: IVoiceEvents;

  /* SIP library objects */
  public static simpleUser: SimpleUser;
  public static session?: Session;

  /* Debug info, not used anywhere */
  protected static disconnectTime = Date.now();
  protected static aliveTime = 0;
  protected static nDisconnects = 0;

  static async initialize() {
    this.isDebug = JSON.parse(localStorage.getItem('HITL-Voice-Debug') || 'false');
    const media = this.getMediaElements();
    const domain = this.domain || this.DEFAULT_DOMAIN;

    const options = {
      aor: `sip:${this.userId}@${domain}`,
      reconnectionAttempts: 30,
      userAgentOptions: {
        allowLegacyNotifications: true,
        /* OneReach user auth token */
        authorizationPassword: this.token,
        authorizationUsername: this.token,
        logBuiltinEnabled: false,
        noAnswerTimeout: 60,
        sipExtension100rel: SIPExtension.Supported,
        sipExtensionReplaces: SIPExtension.Supported,
      },
      media: {
        local: {
          video: media.local,
        },
        remote: {
          video: media.remote,
        },
      },
      delegate: WebRTCClient,
    };
    try {
      const wssAddress = this.wssAddress || this.DEFAULT_WSS_ADDRESS;
      const simpleUser = new SimpleUser(wssAddress, options);

      this.simpleUser = this.patchSimpleUser(simpleUser);
      await this.simpleUser.connect();
      await this.simpleUser.register();
    } catch (err) {
      this.debug('Connection Error', { err });
    }
  }

  public static async hangup() {
    this.debug('Hanging Up...');
    this.session && await this.simpleUser.hangup();
  }

  public static async answer() {
    this.debug('Answering...');
    this.session && await this.simpleUser.answer();
  }

  public static mute() {
    this.debug('Mute');
    this.session && this.simpleUser.mute();
  }

  public static unmute() {
    this.debug('Unmute');
    this.session && this.simpleUser.unmute();
  }

  public static async decline() {
    this.debug('Declining');
    await this.simpleUser.decline();
  }

  public static async dtmf(tone: string) {
    this.debug('Playing DTMF...');
    /* TODO: Use ringtone API to play DTMF? */
    this.session && await this.simpleUser.sendDTMF(tone);
  }

  public static reconnect() {
    /* @ts-ignore */
    this.simpleUser.attemptReconnection();
  }

  public static onCallAnswered() {
    this.debug('On Call Answered');
    this.handlers.onJoinCall();
  }

  /** Original callback has no session argument */
  public static onCreateCall(session: Session) {
    if (session) {
      this.debug('On Call Created', session);
      this.session = session;
    }
  }

  /** Original callback has no invitation argument */
  public static async onIncomingCall(invite: Invitation) {
    if (invite) {
      /** TODO: Is this property available? */
      const number = _.get(invite, 'fromNumber')!;
      this.debug('On Call Received', number, invite);
      this.handlers.onIncomingCall({ number });
    }
  }

  public static onCallHangup() {
    this.debug('On Call Hangup');
    delete this.session;
    this.handlers.onLeaveCall();
  }

  public static onServerDisconnect(error: Error) {
    const now = Date.now();
    const sinceLastDisconnect = now - this.disconnectTime;
    this.disconnectTime = now;
    this.nDisconnects += 1;
    this.aliveTime += sinceLastDisconnect;
    const aTTL = this.aliveTime / this.nDisconnects;
    this.debug('On Server Disconnect', {
      now: new Date(),
      nDisconnects: this.nDisconnects,
      sinceLastDisconnect,
      aTTL,
      error,
    });
  }

  /* Log SIP events, no actions performed */
  static onCallHold = (isOnHold: boolean) =>
    WebRTCClient.debug('On Call Hold', isOnHold);
  static onCallDTMFReceived = (message: string) =>
    WebRTCClient.debug('[On Call DTMF Received', { message });
  static onMessageReceived = (message: string) =>
    WebRTCClient.debug('On Message Received', { message });
  static onRegistered = () => WebRTCClient.debug('On Registered');
  static onUnregistered = () => WebRTCClient.debug('On Unregistered');
  static onServerConnect = () => WebRTCClient.debug('On Server Connect');

  protected static getMediaElements() {
    const LOCAL = 'orWebRTCLocalVideo';
    const REMOTE = 'orWebRTCRemoteVideo';

    let local = document.getElementById(LOCAL) as HTMLVideoElement;
    let remote = document.getElementById(REMOTE) as HTMLVideoElement;

    if (!local) {
      local = document.createElement('video');
      local.setAttribute('id', LOCAL);
      local.setAttribute('muted', 'muted');
      local.style.display = 'none';
      document.body.appendChild(local);
    }

    if (!remote) {
      remote = document.createElement('video');
      remote.setAttribute('id', REMOTE);
      remote.style.display = 'none';
      document.body.appendChild(remote);
    }

    return { local, remote };
  }

  protected static debug(...args: any[]) {
    if (this.isDebug) {
      const time = new Date().toLocaleTimeString();
      console.debug(`[WebRTC Client - ${time}]`, ...args);
    }
  }

  /** Session manager provides extra data to delegates that we need */
  protected static patchSimpleUser(user: SimpleUser): SimpleUser {
    // @ts-ignore
    const manager = user.sessionManager as SessionManager;

    manager.delegate!.onCallReceived = this.onIncomingCall.bind(this);
    manager.delegate!.onCallCreated = (session: Session) => {
      // @ts-ignore
      user.session = session;
      this.onCreateCall(session);
    };

    return user;
  }
}
